import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, Plus, X } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const People = ({ label, value, onChange, testid, roles }) => (
  <div className="space-y-2">
    <Label>{label}</Label>
    {value.map((p, i) => (
      <div key={i} className="flex gap-2">
        <Input placeholder="Name" value={p.name} onChange={(e) => onChange(value.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} data-testid={`${testid}-name-${i}`} />
        {roles && <Input placeholder="Role" value={p.role} onChange={(e) => onChange(value.map((x, j) => (j === i ? { ...x, role: e.target.value } : x)))} data-testid={`${testid}-role-${i}`} />}
        <Input placeholder="Phone" value={p.phone} onChange={(e) => onChange(value.map((x, j) => (j === i ? { ...x, phone: e.target.value } : x)))} data-testid={`${testid}-phone-${i}`} />
        <Button type="button" variant="ghost" size="icon" onClick={() => onChange(value.filter((_, j) => j !== i))} aria-label="Remove"><X className="h-4 w-4" /></Button>
      </div>
    ))}
    <Button type="button" variant="outline" size="sm" onClick={() => onChange([...value, { name: "", phone: "", role: "" }])} data-testid={`${testid}-add`}><Plus className="mr-1 h-3 w-3" />Add</Button>
  </div>
);

const blank = { first_aiders: [], first_aid_kit: "", assembly_point: "", contacts: [], notes: "" };

export const SiteContactsForm = ({ address, onSaved }) => {
  const [f, setF] = useState(null);
  const [can, setCan] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/site-info", { params: { address } }).then((r) => { setF({ ...blank, ...(r.data.info || {}) }); setCan(r.data.can_edit); });
  }, [address]);
  if (!f) return null;
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/site-info", { site_address: address, first_aiders: f.first_aiders, first_aid_kit: f.first_aid_kit, assembly_point: f.assembly_point, contacts: f.contacts, notes: f.notes });
      setF({ ...blank, ...data });
      toast.success("Site contacts saved — the QR page shows them now");
      onSaved?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const set = (k) => (v) => setF({ ...f, [k]: v?.target ? v.target.value : v });
  return (
    <div className="space-y-4" data-testid="site-contacts-form">
      <fieldset disabled={!can} className="space-y-4">
        <People label="First aider(s)" value={f.first_aiders} onChange={set("first_aiders")} testid="first-aider" />
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1"><Label>First aid kit location</Label><Input value={f.first_aid_kit} onChange={set("first_aid_kit")} data-testid="first-aid-kit" /></div>
          <div className="space-y-1"><Label>Fire assembly point</Label><Input value={f.assembly_point} onChange={set("assembly_point")} data-testid="assembly-point" /></div>
        </div>
        <People label="Site contacts" value={f.contacts} onChange={set("contacts")} testid="site-contact" roles />
        <div className="space-y-1"><Label>Other emergency notes</Label><Input value={f.notes} onChange={set("notes")} placeholder="e.g. Defibrillator at main reception" data-testid="site-notes" /></div>
      </fieldset>
      {can ? <Button onClick={save} disabled={busy} data-testid="save-site-contacts">{busy ? "Saving…" : "Save site contacts"}</Button> : <p className="text-xs text-muted-foreground">Only managers can change site contacts.</p>}
      {f.updated_at && <p className="text-xs text-muted-foreground">Last updated {new Date(f.updated_at).toLocaleString("en-GB")} by {f.updated_by}</p>}
    </div>
  );
};

export const SiteContactsCard = ({ address }) => (
  <div className="rounded-2xl border bg-card p-5" data-testid="site-contacts-card">
    <p className="mb-1 flex items-center gap-2 font-semibold"><Users className="h-4 w-4 text-health" />Site emergency contacts</p>
    <p className="mb-4 text-sm text-muted-foreground">Shown live on this site's QR emergency page and printed on the A4 poster.</p>
    <SiteContactsForm address={address} />
  </div>
);
